import { HttpClientXHR } from "./config/http.client";
import { GenericHttpClient } from "./config/http.generic.client";

// /complete and /skip post the customer's message through the same path a
// widget answer takes (workflow step, Kai, agent wake-up) before responding —
// give them the same headroom /workflow/answer gets.
const CAPTURE_ROUTING_TIMEOUT_MS = 30000;
const CAPTURE_CLAIM_TIMEOUT_MS = 10000;

export interface CaptureClaimResponse {
  captureRequest?: any;
  claimedElsewhere?: boolean;
}

/**
 * Shared capture-request routes (contract §4). Auth is the usual shared-route
 * header set (Api-Token, Gleap-Id, Gleap-Hash) injected by HttpClientXHR; the
 * server scopes every call to this session's own ticket.
 */
export class HttpCaptureService extends GenericHttpClient<any> {
  static _instance: HttpCaptureService;
  static getInstance(): HttpCaptureService {
    if (this._instance == null) {
      this._instance = new HttpCaptureService("/v3/shared/capture-requests");
    }
    return this._instance;
  }

  private requestPath(id: string, action?: string) {
    return `${this.path}/${encodeURIComponent(id)}${action ? `/${action}` : ""}`;
  }

  async getRequest(id: string): Promise<any> {
    const response = await this.get(this.requestPath(id));
    return response.data;
  }

  async claim(id: string, body: Record<string, any>): Promise<CaptureClaimResponse> {
    const response = await this.post(this.requestPath(id, "claim"), body, {
      timeout: CAPTURE_CLAIM_TIMEOUT_MS,
    });
    return response.data ?? {};
  }

  async sendEvent(
    id: string,
    body: { type: "declined" | "unsupported" | "failed" | "released"; reason?: string },
  ): Promise<void> {
    await this.post(this.requestPath(id, "event"), body, {
      timeout: CAPTURE_CLAIM_TIMEOUT_MS,
    });
  }

  /**
   * pagehide-safe `released` event (XHR is aborted with the unloading
   * document, fetch keepalive survives it), so a capture the customer walked
   * away from doesn't stay "in progress" for a week. Fire-and-forget.
   */
  releaseKeepalive(id: string): void {
    try {
      if (typeof fetch !== "function") return;
      const headers = this.injectToken({
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      });
      const stringHeaders: Record<string, string> = {};
      Object.keys(headers).forEach((key) => {
        stringHeaders[key] = String(headers[key]);
      });
      fetch(`${HttpClientXHR.baseURL}${this.requestPath(id, "event")}`, {
        method: "POST",
        keepalive: true,
        headers: stringHeaders,
        body: JSON.stringify({ type: "released", reason: "pagehide" }),
      }).catch(() => {});
    } catch (_) {}
  }

  // Not retried: a timed-out skip may well have posted the customer's
  // "not now" message already, and a second one would land as a 410 anyway.
  async skip(id: string, message: string): Promise<any> {
    const response = await this.post(
      this.requestPath(id, "skip"),
      { message },
      { timeout: CAPTURE_ROUTING_TIMEOUT_MS, retry: false },
    );
    return response.data;
  }

  // Not retried by the client: /complete is idempotent for the same files, but
  // the store decides itself whether a repeat is safe (see Stores/Capture).
  async complete(id: string, body: Record<string, any>): Promise<any> {
    const response = await this.post(this.requestPath(id, "complete"), body, {
      timeout: CAPTURE_ROUTING_TIMEOUT_MS,
      retry: false,
    });
    return response.data;
  }
}
